//#region IMPORT
import React, { useRef, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Capacitor } from '@capacitor/core';

/** Components Import */
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import useThrottle from '../../../Hooks/useThrottle';
import PerButton from '../../Permit/ui/PerButton';
import BackClickBottomSheet from '../../Permit/ui/BackClickBottomSheet';
//#endregion

const MAX_PHOTOS = 5;

function M_WOS_BASIC_01(props) {
  const dispatch = useDispatch();
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const fileInputRef = useRef(null);
  console.log(props.selectedTargets);

  const [currentStep, setCurrentStep] = useState(1);

  const tabLists = useMemo(() => {
    const getListByStep = (step) => {
      const seen = new Set();
      const list = [];

      (props.masterData || []).forEach((item) => {
        if (!item.pathArray) return;
        const name = item.pathArray[step - 1];
        if (!name) return;

        const currentPathKey = item.pathArray.slice(0, step).join(' > ');

        if (!seen.has(currentPathKey)) {
          if (item.depth === step) {
            seen.add(currentPathKey);
            list.push({
              ...item,
              displayName: name,
              selectedPath: currentPathKey,
              displayDepth: step,
              pathSlice: item.pathArray.slice(0, step),
            });
          }
        }
      });
      return list.sort((a, b) => a.displayName.localeCompare(b.displayName));
    };

    return {
      1: getListByStep(1),
      2: getListByStep(2),
      3: getListByStep(3),
      4: getListByStep(4),
    };
  }, [props.masterData]);
  //#endregion

  const handleToggleSelect = (item) => {
    const isSelected = props.selectedTargets.some(
      (m) =>
        m.selectedPath === item.selectedPath &&
        m.displayDepth === item.displayDepth
    );

    if (isSelected) {
      props.setSelectedTargets(
        props.selectedTargets.filter(
          (m) =>
            !(
              m.selectedPath === item.selectedPath &&
              m.displayDepth === item.displayDepth
            )
        )
      );
    } else {
      let newList = [...props.selectedTargets];

      newList = newList.filter((m) => {
        const isParent = item.selectedPath.startsWith(m.selectedPath + ' > ');
        const isChild = m.selectedPath.startsWith(item.selectedPath + ' > ');

        return !isParent && !isChild;
      });

      props.setSelectedTargets([...newList, item]);
    }
  };
  //#endregion

  const [handleSave, isThrottling] = useThrottle(() => {
    if (props.selectedTargets.length === 0) {
      dispatch(
        showSnackbar({ message: '대상 조직을 선택하세요.', type: 'warning' })
      );
      return;
    }
    props.SaveLogic1();
  }, 1000);

  return (
    <Box className="min-h-screen bg-[#f8fafc] pb-10 font-sans text-[#1e293b]">
      <main className="space-y-7 px-5">
        <section className="animate-in fade-in slide-in-from-top duration-700">
          <div className="flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-4">
            <span
              className="material-symbols-outlined fill-1 text-red-500"
              style={{ fontVariationSettings: "'FILL' 1" }}
            >
              warning
            </span>
            <p className="text-[13px] leading-snug font-medium text-red-700">
              위험 요소 발견 시 즉시 작업을 중지하고 보고해 주세요. 당신의
              안전이 최우선입니다.
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-end justify-between">
            <h2 className="text-[17px] font-bold text-slate-800">현장 사진</h2>
            <span className="text-[11px] font-semibold text-slate-400">
              {
                props.workStopUpload.previewList.filter((img) => !img.deleted)
                  .length
              }{' '}
              / {MAX_PHOTOS}
            </span>
          </div>
          <div className="flex gap-3 overflow-hidden">
            <button
              className="flex aspect-square min-w-22.5 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-slate-200 bg-white transition-transform active:scale-95"
              onClick={() => {
                if (isThrottling) return;
                const count = props.workStopUpload.previewList.filter(
                  (img) => !img.deleted
                ).length;
                if (count >= MAX_PHOTOS) return;
                Capacitor.isNativePlatform()
                  ? props.workStopUpload?.NativeFileChange()
                  : fileInputRef.current?.click();
              }}
            >
              <span className="material-symbols-outlined text-slate-400">
                add_a_photo
              </span>
              <span className="text-2xs font-bold text-slate-400">
                사진 추가
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={props.workStopUpload.FileChange}
            />
            <Swiper slidesPerView="auto" spaceBetween={8} className="flex-1">
              {props.workStopUpload.previewList
                .filter((img) => !img.deleted)
                .map((item, index) => (
                  <SwiperSlide key={index} style={{ width: '90px' }}>
                    <div className="relative aspect-square overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                      <img
                        src={item.preview}
                        className="h-full w-full object-cover"
                        alt="preview"
                      />
                      <button
                        className="absolute top-1 right-1 rounded-full bg-black/30 p-0.5 text-white"
                        onClick={() => props.workStopUpload.DeletePreview(item)}
                      >
                        <span className="material-symbols-outlined text-sm">
                          close
                        </span>
                      </button>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-[17px] font-bold text-slate-800">
            내용 <span className="text-xs text-red-500">*</span>
          </h2>
          <textarea
            className="h-32 w-full resize-none rounded-2xl border border-slate-200 bg-white p-4 text-base transition-all outline-none focus:border-[#82B964] focus:ring-2 focus:ring-green-500/20"
            placeholder="작업 중지 사유 및 현장 상황을 상세히 입력해 주세요."
            value={props.workStopInputs.stopComment || ''}
            onChange={(e) =>
              props.setWorkStopInputs({
                ...props.workStopInputs,
                stopComment: e.target.value,
              })
            }
          />
        </section>

        <section className="space-y-2">
          <h2 className="text-lg font-bold text-slate-800">대상 지정</h2>
          <div className="flex items-center gap-1.5 rounded-full bg-green-50 px-3">
            <span className="material-symbols-outlined text-[14px] text-[#82B964]">
              verified
            </span>
            <span className="text-[11px] font-bold text-[#4a6b3a]">
              상위 조직 선택 시 하위 부서원이 모두 포함됩니다
            </span>
          </div>
          <div className="rounded-4xl border border-slate-50 bg-white px-4 py-4 shadow-sm">
            <div className="mb-4 flex items-center gap-1.5 rounded-2xl bg-slate-50 p-1">
              {[
                { id: 1, n: '본부' },
                { id: 2, n: '부서' },
                { id: 3, n: '팀' },
                { id: 4, n: '파트' },
              ].map((step) => (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex-1 rounded-xl py-2 text-[12px] font-bold transition-all ${currentStep === step.id ? 'bg-[#82B964] text-white shadow-md' : 'text-slate-400'}`}
                >
                  {step.n}
                </button>
              ))}
            </div>

            <div className="hide-scrollbar flex h-60 flex-col gap-1.5 space-y-2.5 overflow-y-auto pr-1">
              {tabLists[currentStep].map((item, idx) => {
                const isSelected = props.selectedTargets.some(
                  (m) =>
                    m.selectedPath === item.selectedPath &&
                    m.displayDepth === item.displayDepth
                );
                return (
                  <button
                    key={idx}
                    onClick={() => handleToggleSelect(item)}
                    className={`flex w-full items-center justify-between rounded-2xl border p-4 transition-all active:scale-[0.98] ${isSelected ? 'border-[#82B964] bg-green-50/50' : 'border-transparent bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-3 text-left">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ${isSelected ? 'text-[#82B964]' : 'text-slate-400'}`}
                      >
                        <span className="material-symbols-outlined">
                          corporate_fare
                        </span>
                      </div>
                      <div>
                        <p
                          className={`text-[14px] font-bold ${isSelected ? 'text-[#2c4e1a]' : 'text-slate-800'}`}
                        >
                          {item.displayName}
                        </p>
                        <p className="text-2xs text-slate-400 uppercase">
                          {item.pathArray[0]}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${isSelected ? 'border-[#82B964] bg-[#82B964]' : 'border-slate-200 bg-white'}`}
                    >
                      {isSelected && (
                        <span className="material-symbols-outlined text-[16px] text-white">
                          check
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="space-y-3 pb-10">
          <h3 className="px-1 text-[13px] font-bold text-slate-400">
            등록 대상 목록
          </h3>
          <div className="hide-scrollbar h-50 space-y-2.5 overflow-y-auto rounded-2xl border border-dashed border-slate-200 p-2">
            {props.selectedTargets.length === 0 ? (
              <div className="flex h-full items-center justify-center text-[13px] text-slate-300">
                선택된 대상이 없습니다.
              </div>
            ) : (
              props.selectedTargets.map((member, idx) => (
                <div
                  key={idx}
                  className="animate-in fade-in zoom-in flex items-center justify-between rounded-2xl border border-slate-100 bg-white p-4 shadow-sm duration-300"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {member.pathSlice.map((pathName, pIdx) => (
                        <React.Fragment key={pIdx}>
                          <span className="text-[13px] font-bold text-slate-700">
                            {pathName}
                          </span>
                          {pIdx < member.pathSlice.length - 1 && (
                            <span className="material-symbols-outlined text-[12px] text-slate-300">
                              chevron_right
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <span className="text-2xs rounded-full bg-green-50 px-2.5 py-0.5 font-bold whitespace-nowrap text-[#82B964]">
                      {member.displayDepth === 1
                        ? '본부'
                        : member.displayDepth === 2
                          ? '부서'
                          : member.displayDepth === 3
                            ? '팀'
                            : member.displayDepth === 4
                              ? '파트'
                              : `${member.displayDepth}단계`}
                    </span>
                  </div>
                  <button
                    className="text-slate-300 transition-colors hover:text-red-500"
                    onClick={() => handleToggleSelect(member)}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      close
                    </span>
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </main>

      <div className="fixed bottom-0 left-0 z-50 w-full border-t border-slate-100 bg-background-light px-5 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
        <PerButton
          saveText={'저장'}
          disabled={isThrottling}
          onCancel={() =>
            dispatch(
              BottomSheetActions.SetBottomSheetState({
                bottom: true,
                type: 'backClickWos',
              })
            )
          }
          onClick={handleSave}
        />
      </div>

      {bottomSheetState.bottom && bottomSheetState.type === 'backClickWos' && (
        <BackClickBottomSheet
          open={bottomSheetState.bottom}
          route={
            props.location.state?.gubun === 'list'
              ? '/M_WorkStop/list'
              : '/Home'
          }
        />
      )}
    </Box>
  );
}

export default M_WOS_BASIC_01;
